import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// How often to pull fresh permissions for an already-logged-in user. Section
// grants/revocations are applied server-side immediately, but the browser
// only had the snapshot taken at login — without this, a member keeps acting
// on stale permissions (e.g. seeing a Delete button an admin just revoked)
// until they manually log out and back in.
const PERMISSIONS_REFRESH_INTERVAL_MS = 30000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedClusterId');
    setUser(null);
  }, []);

  // Pull the current user's fresh permissions from the server and merge them
  // into the cached snapshot (keep client-only fields like _pw intact — /auth/me
  // doesn't return them). Silently no-ops on failure (e.g. offline blip); a 401
  // is already handled globally by the API interceptor.
  const userRef = useRef(user);
  userRef.current = user;
  const refreshUser = useCallback(async () => {
    if (!userRef.current) return;
    try {
      const { data } = await authAPI.me();
      setUser((prev) => {
        if (!prev) return prev;
        const merged = { ...prev, ...data };
        // Only commit (and only create a NEW object reference) when something actually
        // changed — an unconditional `{...prev, ...data}` ran every 30s and produced a
        // fresh `user` object even when permissions were identical to before. That new
        // reference cascaded: ClusterContext's `useEffect([user])` re-ran, re-fetched
        // /clusters, and re-selected the cluster with a freshly-fetched (also new-
        // reference) object — which in turn re-triggered any page whose data-fetch
        // effect depended on the whole `selectedCluster` object rather than its .id,
        // causing a full, visible reload every 30s with no real change underneath.
        const unchanged = Object.keys(data).every(k => prev[k] === merged[k]);
        if (unchanged) return prev;
        localStorage.setItem('user', JSON.stringify(merged));
        return merged;
      });
    } catch {
      // network hiccup or session already invalidated — next interval retries
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const tick = () => refreshUser();
    const interval = setInterval(tick, PERMISSIONS_REFRESH_INTERVAL_MS);
    // Also refresh whenever the tab regains focus — catches "admin changed my
    // permissions while I was on another tab" without waiting for the interval.
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', tick);
    };
  }, [user, refreshUser]);

  // role checks
  const isSuperAdmin = user?.rbac_role === 'superadmin';
  const isAdmin = user?.rbac_role === 'admin' || isSuperAdmin;
  // True only for admins appointed directly by a superadmin. A self-service
  // admin (created/promoted by another admin) is not prime — use this to gate
  // deleting/demoting a fellow admin in the UI (backend enforces it too).
  const isPrimeAdmin = isSuperAdmin || !!user?.is_prime_admin;

  const hasSection = useCallback((section) => {
    if (!user) return false;
    // Superadmin is not bound by org entitlement.
    if (user.rbac_role === 'superadmin') return true;
    // Org entitlement gate: the section must be enabled for the user's org.
    const orgSections = user.org_sections || [];
    if (!orgSections.includes(section)) return false;
    // Within the org's entitled set, admins see everything; members need a grant.
    if (user.rbac_role === 'admin') return true;
    return (user.permissions || []).includes(section);
  }, [user]);

  // Verb-level check: can the user perform `action` (view/create/edit/delete)
  // on `section`, per the org's allocated actions (set by superadmin).
  const hasSectionAction = useCallback((section, action) => {
    if (!user) return false;
    if (user.rbac_role === 'superadmin') return true;
    // Server-computed effective verbs per section (org cap already applied;
    // for members it is grant ∩ org). Single source of truth for UI gating.
    const eff = user.effective_section_actions || {};
    return (eff[section] || []).includes(action);
  }, [user]);

  const hasClusterAccess = useCallback((clusterId) => {
    if (!user) return false;
    if (['admin', 'superadmin'].includes(user.rbac_role)) return true;
    const ids = user.cluster_access_ids || [];
    // Empty whitelist means NO access (not all access) for restricted roles
    if (ids.length === 0) return false;
    return ids.includes(clusterId);
  }, [user]);

  const organizationId   = user?.organization_id || 1;
  const organizationName = user?.organization_name || '';

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isSuperAdmin,
      isAdmin,
      isPrimeAdmin,
      hasSection,
      hasSectionAction,
      hasClusterAccess,
      organizationId,
      organizationName,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
